/**
 * Бічна панель (drawer) додавання/редагування умови купівлі-продажу.
 *
 * Виїжджає справа поверх реєстру; список лишається видимим. Поля моделі
 * показуються умовно: фіксована ↔ формульна, а строк зеленого тарифу й постанова
 * НКРЕКП — лише для типу продажу «зелений тариф». Помилки бекенда (накладання
 * чинних періодів, порушення CHECK) показуються дослівно з відповіді сервера.
 *
 * РЕДИЗАЙН: кольори — лише токени (раніше useFinTheme із власною палітрою),
 * поля й кнопки за стандартом контролів 36/10, іконки Lucide. Структура форми,
 * умовні поля, прев'ю формули та логіка збереження не змінені.
 */
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { API_BASE_URL, authFetch } from '@/services/api';
import { useTheme } from '@/hooks/useTheme';
import { Save, TriangleAlert, X } from 'lucide-react';
import TokenSelect from '@/components/base/TokenSelect';
import {
  type SaleTerm, type AssetLite,
  BUYER_TYPES, SALE_TYPES, PRICE_MODELS, BASE_INDEXES, STATUSES, toDateInput,
} from '../theme';

interface Props {
  open: boolean;
  record: SaleTerm | null;      // null → створення
  assets: AssetLite[];
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
}

type Form = Record<string, string | boolean>;

const blank = (): Form => ({
  asset_id: '', seller_name: '', buyer_type: 'guaranteed', buyer_name: '',
  contract_number: '', contract_date: '', valid_from: '', valid_to: '',
  sale_type: 'green_tariff', green_tariff_until: '', nkrekp_ref: '',
  price_model: 'fixed', fixed_price_uah_mwh: '', base_index: 'rdn', markup_pct: '',
  vat_included: false, status: 'draft',
});

const fromRecord = (r: SaleTerm): Form => ({
  asset_id: r.asset_id ?? '', seller_name: r.seller_name ?? '', buyer_type: r.buyer_type ?? 'guaranteed',
  buyer_name: r.buyer_name ?? '', contract_number: r.contract_number ?? '',
  contract_date: toDateInput(r.contract_date), valid_from: toDateInput(r.valid_from),
  valid_to: toDateInput(r.valid_to), sale_type: r.sale_type ?? 'green_tariff',
  green_tariff_until: toDateInput(r.green_tariff_until), nkrekp_ref: r.nkrekp_ref ?? '',
  price_model: r.price_model ?? 'fixed',
  fixed_price_uah_mwh: r.fixed_price_uah_mwh == null ? '' : String(r.fixed_price_uah_mwh),
  base_index: r.base_index ?? 'rdn', markup_pct: r.markup_pct == null ? '' : String(r.markup_pct),
  vat_included: !!r.vat_included, status: r.status ?? 'draft',
});

export default function SaleTermsDrawer({ open, record, assets, canEdit, onClose, onSaved }: Props) {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [form, setForm] = useState<Form>(blank());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) { setForm(record ? fromRecord(record) : blank()); setErr(null); }
  }, [open, record]);

  const set = (k: string, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));
  const isFormula = form.price_model === 'formula';
  const isGreen = form.sale_type === 'green_tariff';

  const preview = useMemo(() => {
    if (!isFormula) return null;
    const base = form.base_index === 'rdn' ? i18n.t('ak_fin2_dam') : i18n.t('ak_fin2_index');
    const p = String(form.markup_pct || '').trim();
    if (!p) return i18n.t('ak_fin2_price_x', { base });
    const n = Number(p);
    const sign = n >= 0 ? '+' : '−';
    return i18n.t('ak_fin2_price', { base, sign, n: Math.abs(n) });
  }, [isFormula, form.base_index, form.markup_pct, t]);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const body = { ...form,
        fixed_price_uah_mwh: form.price_model === 'fixed' ? form.fixed_price_uah_mwh : null,
        base_index: isFormula ? form.base_index : null,
        markup_pct: isFormula ? form.markup_pct : null,
        green_tariff_until: isGreen ? form.green_tariff_until : null,
        nkrekp_ref: isGreen ? form.nkrekp_ref : null,
      };
      const url = record ? `${API_BASE_URL}/financial/sale-terms/${record.id}` : `${API_BASE_URL}/financial/sale-terms`;
      const r = await authFetch(url, {
        method: record ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.message || `HTTP ${r.status}`);
      onSaved();
    } catch (e: any) {
      setErr(e?.message || t('ak_fin2_failed_to_save'));
    } finally { setSaving(false); }
  };

  if (!open) return null;

  const lbl = (t: string) => (
    <div className="text-[10.5px] mb-1" style={{ color: 'var(--text-muted)' }}>{t}</div>
  );
  /** Поле за стандартом контролів §6: 36 / radius 10. */
  const inputStyle = {
    width: '100%', height: 36, borderRadius: 10, background: 'var(--surface-2)',
    color: 'var(--text)', border: '1px solid var(--line)',
    padding: '0 10px', fontSize: 12.5, outline: 'none',
    colorScheme: (isDark ? 'dark' : 'light') as 'dark' | 'light',
  } as const;

  const textField = (k: string, placeholder = '') => (
    <input value={String(form[k] ?? '')} placeholder={placeholder} disabled={!canEdit}
      onChange={(e) => set(k, e.target.value)} style={inputStyle} />
  );
  const dateField = (k: string) => (
    <input type="date" value={String(form[k] ?? '')} disabled={!canEdit}
      onChange={(e) => set(k, e.target.value)} style={inputStyle} />
  );
  // zIndex 80: список має лягати НАД самим дравером (він на 61).
  const selectField = (k: string, opts: { value: string; label: string }[]) => (
    <TokenSelect value={String(form[k] ?? '')} options={opts} disabled={!canEdit}
      onChange={(v) => set(k, v)} width="100%" zIndex={80} />
  );
  /** Секції розділені лінією, а не вкладеною рамкою (§3). */
  const section = (title: string, children: ReactNode) => (
    <div className="mb-4 pb-4" style={{ borderBottom: '1px solid var(--line)' }}>
      <div className="text-[10.5px] font-bold uppercase mb-2"
        style={{ color: 'var(--text-dim)', letterSpacing: '0.06em' }}>{title}</div>
      {children}
    </div>
  );

  return (
    <>
      <div onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 60,
                 background: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(40,50,60,0.35)' }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(560px, 100%)', zIndex: 61,
        background: 'var(--surface-solid)', borderLeft: '1px solid var(--line)',
        display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow)',
        fontFamily: 'Urbanist, Inter, sans-serif',
      }}>
        {/* шапка */}
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid var(--line)' }}>
          <div className="font-display text-base font-semibold" style={{ color: 'var(--text)' }}>
            {record ? t('ak_fin2_edit_term') : t('ak_fin2_new_purchase_and_sale_term')}
          </div>
          <button onClick={onClose} className="flex items-center justify-center cursor-pointer"
            style={{ width: 36, height: 36, borderRadius: 10, color: 'var(--text-muted)',
                     background: 'var(--surface-2)', border: '1px solid var(--line)' }}>
            <X size={17} strokeWidth={2} />
          </button>
        </div>

        {/* тіло */}
        <div className="px-5 py-4 overflow-y-auto" style={{ flex: 1 }}>
          {err && (
            <div className="text-[12px] mb-4 flex items-start gap-2 py-2 pl-3"
              style={{ borderLeft: '3px solid var(--err)', color: 'var(--err)' }}>
              <TriangleAlert size={14} strokeWidth={2} style={{ marginTop: 1, flexShrink: 0 }} />
              <span>{err}</span>
            </div>
          )}

          {section(t('ak_fin2_asset_and_parties'), (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">{lbl(t('ak_fin2_asset_2'))}
                <TokenSelect value={String(form.asset_id ?? '')} disabled={!canEdit}
                  onChange={(v) => set('asset_id', v)} width="100%" zIndex={80}
                  placeholder={t('ak_fin2_select_an_asset')} ariaLabel={t('ak_fin2_asset')}
                  options={assets.map((a) => ({ value: a.id, label: a.assetName }))} />
              </div>
              <div>{lbl(t('ak_fin2_seller'))}{textField('seller_name', t('ak_fin2_short_name'))}</div>
              <div>{lbl(t('ak_fin2_buyer_type'))}{selectField('buyer_type', BUYER_TYPES)}</div>
              <div className="col-span-2">{lbl(t('ak_fin2_buyer'))}{textField('buyer_name', t('ak_fin2_short_name'))}</div>
            </div>
          ))}

          {section(t('ak_fin2_contract_and_period'), (
            <div className="grid grid-cols-2 gap-3">
              <div>{lbl(t('ak_fin2_contract_number'))}{textField('contract_number')}</div>
              <div>{lbl(t('ak_fin2_contract_date'))}{dateField('contract_date')}</div>
              <div>{lbl(t('ak_fin2_valid_from'))}{dateField('valid_from')}</div>
              <div>{lbl(t('ak_fin2_valid_to'))}{dateField('valid_to')}</div>
            </div>
          ))}

          {section(t('ak_fin2_sale_type'), (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">{lbl(t('ak_fin2_sale_type_2'))}{selectField('sale_type', SALE_TYPES)}</div>
              {isGreen && <div>{lbl(t('ak_fin2_green_tariff_validity_end'))}{dateField('green_tariff_until')}</div>}
              {isGreen && <div>{lbl(t('ak_fin2_neurc_resolution_no_and_date'))}{textField('nkrekp_ref')}</div>}
            </div>
          ))}

          {section(t('ak_fin2_price_model'), (
            <>
              <div className="mb-3">{lbl(t('ak_fin2_model_2'))}{selectField('price_model', PRICE_MODELS)}</div>
              {!isFormula ? (
                <div>{lbl(t('ak_fin2_price_uah_mwh'))}{textField('fixed_price_uah_mwh', '0.00')}</div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div>{lbl(t('ak_fin2_base_index'))}{selectField('base_index', BASE_INDEXES)}</div>
                  <div>{lbl(t('ak_fin2_markup_discount'))}{textField('markup_pct', '± X')}</div>
                  {/* Прев'ю формули — акцентна смуга замість вкладеного боксу. */}
                  <div className="col-span-2 text-[12px] py-2 pl-3"
                    style={{ borderLeft: '3px solid var(--series-3)', color: 'var(--text-muted)' }}>
                    {preview} · {t('ak_fin2_rounded_to_2_decimals_final_hourly_pri')}
                  </div>
                </div>
              )}
            </>
          ))}

          <div className="mb-2">
            <div className="text-[10.5px] font-bold uppercase mb-2"
              style={{ color: 'var(--text-dim)', letterSpacing: '0.06em' }}>{t('ak_fin2_other')}</div>
            <div className="grid grid-cols-2 gap-3 items-center">
              <label className="flex items-center gap-2 text-[12.5px] cursor-pointer" style={{ color: 'var(--text)' }}>
                <input type="checkbox" checked={!!form.vat_included} disabled={!canEdit}
                  onChange={(e) => set('vat_included', e.target.checked)} />
                {t('ak_fin2_incl_vat_2')}
              </label>
              <div>{lbl(t('ak_fin2_status'))}{selectField('status', STATUSES)}</div>
            </div>
          </div>
        </div>

        {/* підвал */}
        <div className="flex items-center justify-end gap-2 px-5 py-4" style={{ borderTop: '1px solid var(--line)' }}>
          <button onClick={onClose} className="text-xs px-4 cursor-pointer"
            style={{ height: 36, borderRadius: 10, color: 'var(--text)',
                     background: 'var(--surface-2)', border: '1px solid var(--line)' }}>
            {t('ak_fin2_cancel')}
          </button>
          {canEdit && (
            <button onClick={save} disabled={saving}
              className="text-xs px-4 inline-flex items-center gap-1.5 cursor-pointer"
              style={{ height: 36, borderRadius: 10, color: '#fff',
                       background: 'var(--series-3)', border: '1px solid var(--series-3)',
                       opacity: saving ? 0.6 : 1 }}>
              <Save size={14} strokeWidth={2} />
              {saving ? t('ak_fin2_saving') : record ? t('ak_fin2_save') : t('ak_fin2_create')}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
